import { useState } from "react";
import {
  ChevronDown,
  Clapperboard,
  Image,
  Info,
  PenLine,
  Sparkles,
  Stamp,
  Wand2,
  X,
  type LucideIcon,
} from "lucide-react";

import { Button } from "@/shared/ui/button";
import type { Scene } from "@/shared/lib/advisorScene";

import { useSkillGrid } from "../model/useSkillGrid";
import { splitSkillCards } from "../model/skillGridLayout";
import type { SkillCard } from "../api/advisorGridApi";
import { getPersona, usePersona } from "@/shared/brand/persona";

/** builtin skill → 图标(纯呈现;未知 skill 回落 Sparkles)。中文名/话术走后端,图标不入契约。 */
const ICONS: Record<string, LucideIcon> = {
  "product-poster": Image,
  "write-copy": PenLine,
  "brand-stamp": Stamp,
  "edit-image": Wand2,
  "digital-human": Clapperboard,
};

/** 计费口径 → 是否需积分(per_success_* = 花积分,advisor_chat/free = 不花)。 */
function costsCredits(billing: string): boolean {
  return billing.startsWith("per_success");
}

function SkillButton({
  c,
  onPick,
  onInspect,
}: {
  c: SkillCard;
  onPick: (label: string) => void;
  onInspect?: (c: SkillCard) => void;
}) {
  const Icon = ICONS[c.name] ?? Sparkles;
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-auto justify-start gap-2 py-2 text-left"
      onClick={() => onPick(c.label)}
      title={c.description}
    >
      <Icon className="h-4 w-4 shrink-0 text-primary" aria-hidden />
      <span className="flex-1 truncate">{c.label}</span>
      {costsCredits(c.billing) && (
        <span className="shrink-0 text-xs" aria-label="需积分" title="需积分">
          💎
        </span>
      )}
      {/* ADV-UI-5:有五段稿才给「详情」入口;stopPropagation 与点卡说话分开两条命 */}
      {c.guidance && onInspect && (
        <span
          role="button"
          tabIndex={0}
          aria-label={`查看「${c.label}」详情`}
          className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-primary/10 hover:text-primary"
          onClick={(e) => {
            e.stopPropagation();
            onInspect(c);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              onInspect(c);
            }
          }}
        >
          <Info className="h-3.5 w-3.5" aria-hidden />
        </span>
      )}
    </Button>
  );
}

const MODE_BADGE: Record<string, { label: string; cls: string }> = {
  EXECUTE: {
    get label() {
      return `${getPersona().title}能直接做`;
    },
    cls: "bg-primary/10 text-primary",
  },
  REDIRECT: {
    get label() {
      return `引导使用(${getPersona().title}带你去做)`;
    },
    cls: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
  },
  GUIDE: { label: "讲解答疑", cls: "bg-muted text-muted-foreground" },
};

function GuidanceSection({ title, items }: { title: string; items: string[] }) {
  if (!items.length) return null;
  return (
    <div>
      <div className="mb-0.5 text-2xs font-semibold text-muted-foreground">{title}</div>
      <ul className="space-y-0.5 text-xs leading-relaxed">
        {items.map((it, i) => (
          <li key={i} className="flex gap-1">
            <span className="text-muted-foreground">·</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * ADV-UI-5 五段稿详情面板(能力目录 registry.yaml 单一源,文案原样渲染零加工)。
 * REDIRECT 的「改推谁」在 notSuitableFor 逐条自带「→ 去处」,原样列出。
 * 「就这么办」= 把卡 label 说进对话(与点卡同语义,不是新机制)。
 */
export function SkillGuidancePanel({
  card,
  onPick,
  onClose,
}: {
  card: SkillCard;
  /**
   * 缺省 = 不给「就这么办」按钮(ADV-HUB-SYNC-1:hub 技能**管理**页复用本面板只作查阅,
   * 那页没有对话输入框,给个派单按钮就是半截承诺)。对话抽屉照常传。
   */
  onPick?: (label: string) => void;
  onClose: () => void;
}) {
  const persona = usePersona();
  const g = card.guidance;
  if (!g) return null;
  const badge = MODE_BADGE[g.mode] ?? MODE_BADGE.GUIDE;
  return (
    <div className="space-y-2 rounded-lg border bg-card p-3" data-testid="skill-guidance">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold">{card.label}</span>
        <span className={`rounded-full px-2 py-0.5 text-2xs font-medium ${badge.cls}`}>
          {badge.label}
        </span>
        {g.cost && <span className="ml-auto text-2xs text-muted-foreground">{g.cost}</span>}
        <button
          type="button"
          aria-label="关闭详情"
          onClick={onClose}
          className="rounded p-0.5 text-muted-foreground hover:bg-primary/10 hover:text-primary"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
      <p className="text-xs leading-relaxed">{card.description}</p>
      {g.detail && <p className="text-xs leading-relaxed text-muted-foreground">{g.detail}</p>}
      <GuidanceSection title="什么时候用" items={g.scenarios} />
      <GuidanceSection title="适合" items={g.suitableFor} />
      <GuidanceSection title="不适合(该用什么)" items={g.notSuitableFor} />
      <GuidanceSection title="要准备什么" items={g.inputRequired} />
      <GuidanceSection title="可选补充" items={g.inputOptional} />
      <GuidanceSection title="可以这样说" items={g.examples} />
      {g.mode === "EXECUTE" && onPick && (
        <Button type="button" size="sm" className="w-full" onClick={() => onPick(card.label)}>
          就这么办(把「{card.label}」说给{persona.title})
        </Button>
      )}
    </div>
  );
}

/** 按 category 分组(保持首现顺序)。 */
function groupByCategory(cards: SkillCard[]): [string, SkillCard[]][] {
  const map = new Map<string, SkillCard[]>();
  for (const c of cards) {
    const cat = c.category || "其他";
    if (!map.has(cat)) map.set(cat, []);
    map.get(cat)!.push(c);
  }
  return [...map.entries()];
}

/**
 * 个性化 skill 网格:对话开场 on-ramp。点卡 = 把 label 说进对话(onPick),替用户起个头。
 * D2* 渐进披露:每业务域首屏取 recommended 前 2 个(splitSkillCards),其余收进「更多技能(N)」,点开按
 * category 分组 —— 为 30+ skill 搭架子,首屏不淹没。空/加载中不渲染。
 *
 * <p>场景置顶(2026-07-15):scene = 唤起军师时用户所在页面(shared/lib/advisorScene),其相关 skill 提到
 * 首屏最前。scene 由上层在**打开那一刻快照**后传入并冻结 —— 开着时切页不重排(否则卡片在眼皮底下跳)。
 */
export function SkillGrid({
  onPick,
  scene,
}: {
  onPick: (label: string) => void;
  scene?: Scene | null;
}) {
  const { cards, loading } = useSkillGrid();
  const [showMore, setShowMore] = useState(false);
  const [inspecting, setInspecting] = useState<SkillCard | null>(null);
  if (loading || cards.length === 0) return null;

  const { firstScreen, more } = splitSkillCards(cards, undefined, scene?.skills ?? []);
  // 只有**真的**把场景技能提上来了才解释。场景相关 skill 一张都没下发(HIDDEN/无权限)时闭嘴,
  // 不宣称没做的事。
  const pinnedAny = scene ? firstScreen.some((c) => scene.skills.includes(c.name)) : false;

  return (
    <div className="mb-3 space-y-2" role="group" aria-label="能帮你做的">
      {pinnedAny && (
        <p className="px-0.5 text-2xs text-muted-foreground">
          你在 {scene!.label} · 已把相关技能置顶
        </p>
      )}
      {inspecting && (
        <SkillGuidancePanel
          card={inspecting}
          onPick={(label) => {
            setInspecting(null);
            onPick(label);
          }}
          onClose={() => setInspecting(null)}
        />
      )}
      <div className="grid grid-cols-2 gap-2">
        {firstScreen.map((c) => (
          <SkillButton key={c.name} c={c} onPick={onPick} onInspect={setInspecting} />
        ))}
      </div>

      {more.length > 0 && (
        <>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-full justify-center gap-1 text-xs text-muted-foreground"
            aria-expanded={showMore}
            onClick={() => setShowMore((v) => !v)}
          >
            更多技能({more.length})
            <ChevronDown
              className={`h-3.5 w-3.5 transition-transform ${showMore ? "rotate-180" : ""}`}
            />
          </Button>
          {showMore && (
            <div className="space-y-2">
              {groupByCategory(more).map(([category, list]) => (
                <div key={category} className="space-y-1">
                  <div className="px-0.5 text-2xs font-medium text-muted-foreground">
                    {category}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {list.map((c) => (
                      <SkillButton key={c.name} c={c} onPick={onPick} onInspect={setInspecting} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
